import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Badge, Button, Card, Field, Icon, PageHeader } from "@nelurio/ui";
import { useI18n } from "@nelurio/i18n";
import { adminApi } from "../api";
import { useAdminResource } from "../hooks";
import { ActionNotice } from "../dialogs";
import { ErrorState, LoadingState } from "../components";
import type { SiteConfig, SystemStatus, SubscriptionStats } from "../types";

/** One guided connection step: where it lands, what it checks, what to do. */
const STEPS = [
  { id: "payments", icon: "credit-card" },
  { id: "smtp", icon: "message" },
  { id: "storage", icon: "library" },
  { id: "oauth", icon: "users" },
  { id: "ai", icon: "brain" }
] as const;

/** SMTP step: REAL connection — the relay credentials are stored in the
 *  site config and a test email verifies the path end-to-end. */
function SmtpStep({ config, system, onNotice, probeAgain, done }: {
  config: ReturnType<typeof useAdminResource<SiteConfig>>;
  system: ReturnType<typeof useAdminResource<SystemStatus>>;
  onNotice: (n: { error?: string; info?: string }) => void;
  probeAgain: () => void;
  done: boolean;
}) {
  const { t, formatNumber } = useI18n();
  const [host, setHost] = useState("");
  const [port, setPort] = useState("587");
  const [user, setUser] = useState("");
  const [pass, setPass] = useState("");
  const [from, setFrom] = useState("");
  const [testTo, setTestTo] = useState("");
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState("");
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (config.status !== "ready" || !config.data || hydrated) return;
    setHost(config.data.smtp?.host ?? "");
    setPort(config.data.smtp?.port ?? "587");
    setUser(config.data.smtp?.user ?? "");
    setFrom(config.data.smtp?.from ?? "");
    setHydrated(true);
  }, [config.status, config.data, hydrated]);

  const save = async () => {
    setSaving(true);
    try {
      const smtp: Record<string, string> = { host: host.trim(), port: port.trim() || "587", user: user.trim(), from: from.trim() };
      if (pass.trim() !== "") smtp.pass = pass.trim();
      await adminApi("/site-config", { method: "PUT", body: JSON.stringify({ smtp }) });
      onNotice({ info: t("admin.setup.smtpSaved") });
      probeAgain();
    } catch (reason) {
      onNotice({ error: reason instanceof Error ? reason.message : t("admin.feedback.sendError") });
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTestResult("");
    try {
      await adminApi("/setup/smtp-test", { method: "POST", body: JSON.stringify({ to: testTo.trim() }) });
      setTestResult(t("admin.setup.testSent"));
      probeAgain();
    } catch (reason) {
      setTestResult(reason instanceof Error ? reason.message : t("admin.feedback.sendError"));
    }
  };

  return (
    <>
      <h2>{t("admin.studio.stepSmtp")}</h2>
      <p className="nlp-hint">{t("admin.setup.smtpIntro")}</p>
      <div className="admin-setup__status">{done ? <Badge tone="green">{t("admin.setup.liveNow")}</Badge> : <Badge tone="amber">{t("admin.setup.notWired")}</Badge>}</div>
      <Field label={t("admin.setup.smtpHost")} value={host} placeholder="smtp-relay.brevo.com" onChange={(event) => setHost(event.target.value)} />
      <Field label={t("admin.setup.smtpPort")} value={port} onChange={(event) => setPort(event.target.value)} />
      <Field label={t("admin.setup.smtpUser")} value={user} onChange={(event) => setUser(event.target.value)} />
      <Field label={t("admin.setup.smtpPass")} type="password" value={pass} placeholder={"•••"} hint={t("admin.setup.smtpPassHint")} onChange={(event) => setPass(event.target.value)} />
      <Field label={t("admin.setup.smtpFrom")} value={from} placeholder="support@nelurio.com" onChange={(event) => setFrom(event.target.value)} />
      <div className="admin-form-actions">
        <Button icon="check" loading={saving} disabled={!host.trim()} onClick={() => void save()}>{t("common.save")}</Button>
      </div>
      <h3 className="admin-setup__sub">{t("admin.setup.testTitle")}</h3>
      <div className="nl-field-row admin-setup__testrow">
        <label className="nl-field">
          <span className="nl-field__label">{t("admin.setup.testToAria")}</span>
          <input className="nl-input" type="email" value={testTo} placeholder="you@example.com" aria-label={t("admin.setup.testToAria")} onChange={(event) => setTestTo(event.target.value)} />
        </label>
        <Button className="admin-setup__testsend" variant="secondary" icon="send" disabled={!testTo.trim()} onClick={() => void sendTest()}>{t("admin.setup.testSend")}</Button>
      </div>
      {testResult && <p className="nlp-hint" role="alert">{testResult}</p>}
      {system.data?.probes?.smtp && (
        <p className="nlp-hint">{system.data.probes.smtp.ok ? t("admin.system.probeOk") : t("admin.system.probeFail")} · {formatNumber(system.data.probes.smtp.latencyMs, { maximumFractionDigits: 0 })} ms</p>
      )}
    </>
  );
}

export function SetupWizardPage() {
  const { t, formatNumber } = useI18n();
  const [params, setParams] = useSearchParams();
  const step = params.get("step") ?? "payments";
  const config = useAdminResource<SiteConfig>("/site-config");
  const system = useAdminResource<SystemStatus>("/system?fresh=1");
  const abos = useAdminResource<SubscriptionStats>("/subscriptions/stats?days=30");
  const [notice, setNotice] = useState<{ error?: string; info?: string }>({});
  const sys = system.status === "ready" ? system.data : null;

  // Real done states — a step only ticks when the connection truly exists.
  const done: Record<string, boolean> = {
    payments: Boolean(abos.data?.paymentsActive),
    smtp: Boolean(sys?.integrations?.smtp),
    storage: Boolean(sys?.integrations?.storage),
    oauth: Boolean(sys?.integrations?.oauth),
    ai: Boolean(sys?.integrations?.ai)
  };

  const openStep = (id: string) => setParams({ step: id }, { replace: true });
  const probeAgain = () => system.reload();

  return (
    <div className="admin-page">
      <PageHeader title={t("admin.setup.title")} subtitle={t("admin.setup.subtitle")} />
      <ActionNotice error={notice.error} info={notice.info} onDismiss={() => setNotice({})} />

      {config.status === "error" && <ErrorState message={config.error} onRetry={() => void config.reload()} />}
      {config.status === "loading" && <LoadingState rows={3} />}

      <div className="admin-setup-grid">
        {/* Step rail (Wix onboarding pattern) */}
        <Card className="admin-panel admin-setup__rail">
          <h2>{t("admin.setup.railTitle")}</h2>
          <div className="admin-setup__steps">
            {STEPS.map((entry) => (
              <button key={entry.id} type="button" className={`admin-setup__step ${step === entry.id ? "is-active" : ""}`} onClick={() => openStep(entry.id)}>
                <i className={`studio-checklist__dot ${done[entry.id] ? "is-done" : ""}`} aria-hidden="true"><Icon name={done[entry.id] ? "check" : "plus"} size={13} /></i>
                <span>{t(`admin.studio.step${entry.id.charAt(0).toUpperCase() + entry.id.slice(1)}`)}</span>
                {done[entry.id] ? <Badge tone="green">{t("admin.studio.doneChip")}</Badge> : <Icon name="arrow" size={14} />}
              </button>
            ))}
          </div>
        </Card>

        {/* Active step flow */}
        <Card className="admin-panel admin-setup__flow">
          {step === "payments" && <PaymentsStep config={config} onNotice={setNotice} probeAgain={probeAgain} done={done.payments} />}
          {step === "smtp" && <SmtpStep config={config} system={system} onNotice={setNotice} probeAgain={probeAgain} done={done.smtp} />}
          {step === "storage" && <GuideStep
            title={t("admin.studio.stepStorage")}
            intro={t("admin.setup.r2Intro")}
            lines={["S3_ENDPOINT=…", "S3_REGION=auto", "S3_BUCKET=…", "S3_ACCESS_KEY_ID=…", "S3_SECRET_ACCESS_KEY=…", "S3_PATH_STYLE=true"]}
            file="/etc/nelurio/nelurio.env"
            configured={done.storage}
            probeAgain={probeAgain}
            probe={sys?.probes?.s3}
          />}
          {step === "oauth" && <GuideStep
            title={t("admin.studio.stepOAuth")}
            intro={t("admin.setup.oauthIntro")}
            lines={["GOOGLE_CLIENT_ID=…", "GOOGLE_CLIENT_SECRET=…"]}
            file="/etc/nelurio/nelurio.env"
            configured={done.oauth}
            probeAgain={probeAgain}
            probe={undefined}
          />}
          {step === "ai" && <GuideStep
            title={t("admin.studio.stepAI")}
            intro={t("admin.setup.aiIntro")}
            lines={["OPENAI_API_KEY=…", "OPENAI_BASE_URL=…", "OPENAI_MODEL=…"]}
            file="/etc/nelurio/nelurio.env"
            configured={done.ai}
            probeAgain={probeAgain}
            probe={sys?.probes?.ai}
          />}
        </Card>
      </div>
    </div>
  );
}

/** REAL connection: payment handles save via site-config and go live in the
 *  checkout immediately — this step ticks without touching the server. */
function PaymentsStep({ config, onNotice, probeAgain, done }: {
  config: ReturnType<typeof useAdminResource<SiteConfig>>;
  onNotice: (n: { error?: string; info?: string }) => void;
  probeAgain: () => void;
  done: boolean;
}) {
  const { t, formatDate } = useI18n();
  const [paypalMe, setPaypalMe] = useState("");
  const [zarinpal, setZarinpal] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (config.status !== "ready" || !config.data || hydrated) return;
    setPaypalMe(config.data.payments?.paypalMe ?? "");
    setZarinpal(config.data.payments?.zarinpal ?? {});
    setHydrated(true);
  }, [config.status, config.data, hydrated]);

  const save = async () => {
    setSaving(true);
    onNotice({});
    try {
      const payload: Record<string, unknown> = { topbar: config.data?.topbar ?? { visible: true, text: "", cta: "#pricing" }, pricing: config.data?.pricing ?? {}, payments: { paypalMe: paypalMe.trim(), zarinpal: Object.fromEntries(Object.entries(zarinpal).filter(([, v]) => v.trim() !== "")) } };
      await adminApi("/site-config", { method: "PUT", body: JSON.stringify(payload) });
      onNotice({ info: t("admin.setup.paymentsSaved") });
      await config.reload();
      probeAgain();
    } catch (reason) {
      onNotice({ error: reason instanceof Error ? reason.message : t("admin.website.saveError") });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <h2>{t("admin.studio.stepPayments")}</h2>
      <p className="nlp-hint">{t("admin.setup.paymentsIntro")}</p>
      <div className="admin-setup__status">{done ? <Badge tone="green">{t("admin.setup.liveNow")}</Badge> : <Badge tone="amber">{t("admin.setup.notWired")}</Badge>}</div>
      <Field label={t("admin.setup.paypalLabel")} value={paypalMe} placeholder="amirhoseinkhosravi" hint={t("admin.setup.paypalHint")} onChange={(event) => setPaypalMe(event.target.value)} />
      <h3 className="admin-setup__sub">{t("admin.setup.zarinpalTitle")}</h3>
      {["nelurio", "finello", "nexdeutsch", "washhalle"].map((app) => (
        <Field key={app} label={`${app} — https://zarinp.al/…`} value={zarinpal[app] ?? ""} onChange={(event) => setZarinpal((current) => ({ ...current, [app]: event.target.value }))} />
      ))}
      <div className="admin-form-actions">
        <Button icon="check" loading={saving} onClick={() => void save()}>{t("common.save")}</Button>
      </div>
      {config.data?.updatedAt && <p className="admin-form-meta">{t("admin.website.lastChanged", { at: formatDate(config.data.updatedAt) })}</p>}
    </>
  );
}

/** Server-level steps: the honest path — exact env lines to apply, then the
 *  live probe confirms and the step ticks. */
function GuideStep({ title, intro, lines, file, configured, probeAgain, probe }: {
  title: string;
  intro: string;
  lines: string[];
  file: string;
  configured: boolean;
  probeAgain: () => void;
  probe?: { ok: boolean; latencyMs: number; detail?: string };
}) {
  const { t, formatNumber } = useI18n();
  return (
    <>
      <h2>{title}</h2>
      <p className="nlp-hint">{intro}</p>
      <div className="admin-setup__status">{configured ? <Badge tone="green">{t("admin.setup.liveNow")}</Badge> : <Badge tone="amber">{t("admin.setup.notWired")}</Badge>}</div>
      <ol className="admin-setup__howto">
        <li>
          <span>{t("admin.setup.stepEnvPrefix")} <code>{file}</code>{t("admin.setup.stepEnvSuffix")}</span>
          <pre className="admin-setup__code">{lines.join("\n")}</pre>
        </li>
        <li><span>{t("admin.setup.stepRestart")}</span></li>
        <li><span>{t("admin.setup.stepProbe")}</span></li>
      </ol>
      <div className="admin-form-actions">
        <Button variant="secondary" icon="settings" onClick={probeAgain}>{t("admin.system.recheck")}</Button>
        {probe && <span className="admin-form-meta">{probe.ok ? t("admin.system.probeOk") : t("admin.system.probeFail")} · {formatNumber(probe.latencyMs, { maximumFractionDigits: 0 })} ms{probe.detail ? ` · ${probe.detail}` : ""}</span>}
      </div>
    </>
  );
}
